import React, { useContext } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getEnv } from '@cidade360/core-lib/env';
import {
  SettingsKeys,
  getLocalConfig,
  setLocalConfig,
  removeLocalConfig,
} from '@cidade360/ui-lib/hooks/localConfig';
import { useSessionStore } from '@cidade360/core-lib/repo/session';
import { UserPermissions } from '@cidade360/core-lib/validation';
import i18n from 'i18next';

export type HeaderTitleContextValue = {
  content: React.ReactNode | null;
  setContent: (c: React.ReactNode | null) => void;
};
export const HeaderTitleContext = React.createContext<HeaderTitleContextValue>({
  content: null,
  setContent: () => {},
});

export function HeaderTitle({
  children,
}: {
  children: React.ReactNode | null;
}) {
  const { setContent } = React.useContext(HeaderTitleContext);
  React.useEffect(() => {
    setContent(children);
  }, [children, setContent]);
  return null;
}

export function HeaderIconTitle({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactElement<any> | null;
}) {
  const theme = useTheme();
  const Icon: any = icon;
  return (
    <ListItem
      component="div"
      css={{
        width: 'auto',
        color: theme.palette.grey[600],
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      }}
    >
      <ListItemIcon css={{ minWidth: 32 }}>{Icon}</ListItemIcon>
      <ListItemText primary={title} />
    </ListItem>
  );
}

export function HeaderTitleRenderer() {
  const { content } = React.useContext(HeaderTitleContext);

  return <>{content}</>;
}

export function HeaderTitleContextContainer(props: {
  children?: React.ReactNode;
}) {
  const [headerTitleContent, setHeaderTitleContent] =
    React.useState<React.ReactNode | null>(null);

  return (
    <HeaderTitleContext.Provider
      value={{
        content: headerTitleContent,
        setContent: setHeaderTitleContent,
      }}
    >
      {props?.children}
    </HeaderTitleContext.Provider>
  );
}

export enum ChatQueryPrams {
  openChatWithUserID = 'openChatWith',
}
export enum ChatVariant {
  double = 'double',
  single = 'single',
  wrap = 'wrap',
}
export enum ChatMessageSize {
  small = 'small',
  large = 'large',
}
export enum ChatPosition {
  right = 'right',
  left = 'left',
}

export type ChatData = {
  userID: string | null;
  appoID?: string;
  apscID?: string;
  megrID?: string;
  variant?: ChatVariant;
  messageSize?: ChatMessageSize;
  chatPosition?: ChatPosition;
  classes?: {
    chatWindow?: string;
  };
  onPressActions?: () => void;
};

export type AppointmentData = {
  appoID: string;
  patiID: string;
  section?: 'doctorNote';
};

export enum Module {
  survey = 'survey',
  security = 'security',
  operations = 'operations',
  planning = 'planning',
  budgetPlanning = 'budgetPlanning',
}

export function getModuleLabel(mod: Module): string {
  return (
    {
      [Module.survey]: i18n.t('navigation:surveys'),
      [Module.security]: i18n.t('navigation:settings'),
      [Module.operations]: i18n.t('navigation:operations'),
      [Module.planning]: i18n.t('navigation:planningModule'),
      [Module.budgetPlanning]: i18n.t('navigation:budgetPlanningModule'),
    }[mod] ?? mod
  );
}

export type ModulesEnabled = {
  [Module.survey]: boolean;
  [Module.security]: boolean;
  [Module.operations]: boolean;
  [Module.planning]: boolean;
  [Module.budgetPlanning]: boolean;
};

export type OnChangeInstID = (instID?: string) => void;

export const CompleteView = 'complete_view';

export type UIContextValue = {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  setAsUserID: (userID?: string) => void;
  setInstID: (instID?: string) => void;
  registerOnChangeInstID: (onChangeInstID: OnChangeInstID) => void;
  unRegisterOnChangeInstID: (onChangeInstID: OnChangeInstID) => void;
  modules: ModulesEnabled;
  asUserID?: string;
  instID?: string;
};

export const UIContext = React.createContext<UIContextValue>({
  sidebarOpen: true,
  setSidebarOpen: () => {},
  setAsUserID: () => {},
  setInstID: () => {},
  registerOnChangeInstID: () => undefined,
  unRegisterOnChangeInstID: () => undefined,
  modules: {
    [Module.survey]: true,
    [Module.security]: true,
    [Module.operations]: true,
    [Module.planning]: true,
    [Module.budgetPlanning]: true,
  },
});

export function UIContextContainer(props: { children?: React.ReactNode }) {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [asUserID, setAsUserID] = React.useState<string>();
  const [instID, setInstID] = React.useState<string>();
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(isLg);
  const instIDRef = React.useRef<string>();
  const onChangeInstIDRef = React.useRef<OnChangeInstID>();
  const { roles, user } = useSessionStore();
  const isAdmin = roles.includes(UserPermissions.admin);
  const currentUserID = React.useMemo(() => user?.userID, [user]);

  const modules = React.useMemo(() => {
    const env = getEnv();
    return {
      survey: env.DISABLE_SURVEY_MODULE !== 'true',
      security: env.DISABLE_SECURITY_MODULE !== 'true',
      operations: env.DISABLE_OPERATIONS_MODULE !== 'true',
      planning: env.DISABLE_PLANNING_MODULE !== 'true',
      budgetPlanning: env.DISABLE_PLANNING_MODULE !== 'true',
    };
  }, []);

  const setStateAsUserID = React.useCallback(
    (userID?: string) => {
      const isCompleteView = userID === CompleteView;
      let id = !isCompleteView ? userID : undefined;
      if (!isAdmin && id && userID !== currentUserID) {
        id = currentUserID;
      }
      setAsUserID(id);
      setInstID(undefined);
      const hasInstIDChanged = instIDRef.current !== undefined;
      instIDRef.current = undefined;
      if (!isCompleteView) {
        removeLocalConfig(SettingsKeys.GlobalInstID);
        if (hasInstIDChanged) {
          onChangeInstIDRef.current?.(undefined);
        }
      }
      if (isCompleteView) {
        id = userID;
      }
      if (id) {
        setLocalConfig(SettingsKeys.AsUserID, id);
      } else {
        removeLocalConfig(SettingsKeys.AsUserID);
      }
    },
    [isAdmin, currentUserID]
  );

  const setStateInstID = React.useCallback((institutionID?: string) => {
    const hasInstIDChanged = instIDRef.current !== institutionID;
    instIDRef.current = institutionID;
    setInstID(institutionID);
    setAsUserID(undefined);
    removeLocalConfig(SettingsKeys.AsUserID);
    if (institutionID) {
      setLocalConfig(SettingsKeys.GlobalInstID, institutionID);
    } else {
      removeLocalConfig(SettingsKeys.GlobalInstID);
    }

    if (hasInstIDChanged) {
      onChangeInstIDRef.current?.(institutionID);
    }
  }, []);

  const registerOnChangeInstID = React.useCallback(
    (onChangeInstID: OnChangeInstID) => {
      onChangeInstIDRef.current = onChangeInstID;
    },
    []
  );

  const unRegisterOnChangeInstID = React.useCallback(
    (onChangeInstID: OnChangeInstID) => {
      if (onChangeInstIDRef.current === onChangeInstID) {
        onChangeInstIDRef.current = undefined;
      }
    },
    []
  );

  React.useEffect(() => {
    if (!isLg) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isLg]);

  React.useEffect(() => {
    let userID = getLocalConfig(SettingsKeys.AsUserID)?.value;
    if (userID) {
      let id = userID !== CompleteView ? userID : undefined;
      if (!isAdmin && id && userID !== currentUserID) {
        id = currentUserID;
      }
      setAsUserID(id);
    } else {
      const savedInstID = getLocalConfig(SettingsKeys.GlobalInstID)?.value;
      instIDRef.current = savedInstID;
      setInstID(savedInstID);
    }
  }, [currentUserID, isAdmin]);

  return (
    <UIContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        modules,
        asUserID,
        setAsUserID: setStateAsUserID,
        instID,
        setInstID: setStateInstID,
        registerOnChangeInstID,
        unRegisterOnChangeInstID,
      }}
    >
      {props?.children}
    </UIContext.Provider>
  );
}

export const useUIContext = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIContextContainer');
  }
  return context;
};
